import { Box, BoxProps, Theme } from '@mui/material';
import { SxProps } from '@mui/system';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { NavigableElement } from '../../../CfDataGridProps';
import { ValueGetter } from '../CfDataGridTableViewProps';
import { CfDataGridSkeletonRow } from './CfDataGridSkeletonRow';
import { CfDataGridRowProps } from './common';

const dragDropTableRowSx = {
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
  alignItems: 'center',
} as SxProps<Theme>;

export function CfDataGridDragDropRow<TItem>(props: {
  draggableProps: BoxProps;
  dragHandleProps?: BoxProps;
  rowProps: CfDataGridRowProps<TItem>;
}): JSX.Element {
  const rowProps = props.rowProps;
  const item = rowProps.item;
  if (!item) {
    return <CfDataGridSkeletonRow tableViewDefinition={rowProps.tableViewDefinition} itemIndex={rowProps.itemIndex} />;
  }

  const bodyTableRowProps =
    typeof rowProps.tableViewDefinition.bodyTableRowProps === 'function'
      ? rowProps.tableViewDefinition.bodyTableRowProps(rowProps.item)
      : rowProps.tableViewDefinition.bodyTableRowProps;

  const sx = {
    borderTop: (theme) => '1px solid ' + theme.palette.coolGrey[200],
    backgroundColor: 'white',
    padding: 1,
    ...dragDropTableRowSx,
    ...(bodyTableRowProps?.sx ?? {}),

    height: 'inherit',
  } as SxProps<Theme>;

  return (
    <Box
      onClick={() => rowProps.onItemClicked?.(item, 'table', rowProps.itemIndex)}
      onKeyDown={(event: React.KeyboardEvent<HTMLTableRowElement>) =>
        event.key === 'Enter' && rowProps.onItemClicked?.(item, 'table', rowProps.itemIndex)
      }
      {...bodyTableRowProps}
      {...props.draggableProps}
      sx={sx}
    >
      {rowProps.valueGetters.map((getter: ValueGetter<TItem>, index: number) => (
        <Box
          key={index}
          sx={{ textAlign: getter.column.horizontalAlignment, width: getter.column.width, padding: 1 }}
          {...(typeof getter.column.bodyCellProps === 'function'
            ? getter.column.bodyCellProps(item)
            : getter.column.bodyCellProps)}
        >
          {getter.getValue({
            item: item,
            itemIndex: rowProps.itemIndex,
            filterText: rowProps.filterText,
            setNavigableElement: (navigableElement: NavigableElement) =>
              rowProps.setNavigableElement(navigableElement, rowProps.itemIndex, index),
            recomputeRowHeights: undefined,
            recomputeRowHeight: undefined,
            dragHandleProps: props.dragHandleProps,
          })}
        </Box>
      ))}
    </Box>
  );
}

export function CfDataGridDragDropCustomRow(props: {
  content: ReactNode;
  tableRowSxProps?: SxProps<Theme>;
  draggableProps?: BoxProps;
  dragHandleProps?: BoxProps;
}): JSX.Element {
  return (
    <Box
      className='CfTableRow-custom'
      sx={{ borderTop: 'none', p: 0, ...props.tableRowSxProps, ...dragDropTableRowSx } as SxProps<Theme>}
      {...props.draggableProps}
      {...props.dragHandleProps}
    >
      <Box sx={{ width: '100%' }}>{props.content}</Box>
    </Box>
  );
}

export function CfDataGridDragDropRowFooter(props: {
  content: ReactNode;
  tableRowSxProps?: SxProps<Theme>;
  draggableProps?: BoxProps;
  dragHandleProps?: BoxProps;
}): JSX.Element {
  const ref = useRef<HTMLTableRowElement>(null);
  const [hasFocus, setHasFocus] = useState(false);
  const [hasHover, setHasHover] = useState(false);

  useEffect(() => {
    if (ref.current && ref.current.previousElementSibling) {
      const siblingClasses = ref.current.previousElementSibling.classList;
      if (hasFocus) {
        siblingClasses.add('CfTableRow-footerFocus');
      } else {
        siblingClasses.remove('CfTableRow-footerFocus');
      }
      if (hasHover) {
        siblingClasses.add('CfTableRow-footerHover');
      } else {
        siblingClasses.remove('CfTableRow-footerHover');
      }
    }
  }, [hasFocus, hasHover]);

  return (
    <Box
      sx={{ p: 0, ...props.tableRowSxProps, ...dragDropTableRowSx } as SxProps<Theme>}
      ref={ref}
      className='CfTableRow-footer'
      onFocus={() => setHasFocus(true)}
      onBlur={() => setHasFocus(false)}
      onMouseEnter={() => setHasHover(true)}
      onMouseLeave={() => setHasHover(false)}
      {...props.draggableProps}
      {...props.dragHandleProps}
    >
      <Box sx={{ width: '100%' }}>{props.content}</Box>
    </Box>
  );
}
